/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview ContactsData component for managing company contact persons.
 * This component handles primary and additional contact person information including
 * names, titles, departments, phone numbers, and email addresses with visibility
 * toggle controls for public profile display.
 */

import React from "react";
import AdditionalContact from "./AdditionalContact";
import AdditionalContactsList from "./AdditionalContactsList";
import PrimaryContactFields from "./PrimaryContactFields";
import styles from "./ContactsData.module.css";
import { useContactsDataForm } from "./useContactsDataForm";

/**
 * ContactsData component for displaying and editing company contact persons.
 * Manages primary contact and additional contacts with personal details,
 * job information, and communication preferences with visibility toggles.
 *
 * @param {Object} props - Component props
 * @param {Object} props.company - Company data object containing contact persons data
 * @param {Function} props.onFieldChange - Callback function triggered when a field value changes
 * @param {Function} props.onValidationChange - Callback function triggered when validation state changes
 * @param {number} [props.selectedProfile=0] - Currently selected company profile index for multi-profile support
 * @returns {JSX.Element} The rendered ContactsData form section
 */
const ContactsData = ({
  company,
  onFieldChange,
  onValidationChange,
  selectedProfile = 0,
}) => {
  const form = useContactsDataForm({
    company,
    onFieldChange,
    onValidationChange,
    selectedProfile,
  });

  return (
    <div className={styles.sectionContent}>
      <PrimaryContactFields form={form} onFieldChange={onFieldChange} />
      <AdditionalContactsList
        AdditionalContact={AdditionalContact}
        additionalContacts={form.additionalContacts}
        onRemove={form.handleRemoveContact}
        onAdd={form.handleAddContact}
        onContactChange={form.handleAdditionalContactChange}
        titleOptions={form.titleOptions}
        genderOptions={form.genderOptions}
        areaOptions={form.areaOptions}
        getFieldValidationState={form.getAdditionalContactFieldValidationState}
        getErrorMessage={form.getAdditionalContactErrorMessage}
      />
    </div>
  );
};

export default ContactsData;
